import { APIRoutes } from "@/api";
import { followingUsersSchema } from "@/controllers/user/schema";
import { queryClient } from "@/lib/query-client";
import { ApiService } from "@/services/api/api-service";
import {
  QueryFunctionContext,
  useInfiniteQuery as useInfiniteQueryTanstack,
} from "@tanstack/react-query";
import { z } from "zod";

const pageParamSchema = z.object({
  cursor: z.number(),
});

type TPageParam = z.infer<typeof pageParamSchema> | undefined;
type QueryKey = ReturnType<typeof UserFollowingQuery.keys.user>;

async function getFollowing({
  pageParam,
  queryKey,
  signal,
}: QueryFunctionContext<QueryKey, TPageParam>) {
  let url: string = APIRoutes.USER_FOLLOWING;
  const params = new URLSearchParams({
    userId: queryKey[1],
  });

  const pageParamsResult = pageParamSchema.safeParse(pageParam);
  if (pageParamsResult.success) {
    params.set("cursor", pageParamsResult.data.cursor.toString());
  }

  const data = await fetch(`${url}?${params.toString()}`, {
    cache: "no-cache",
    signal,
  }).then((res) => res.json());

  const response = ApiService.parseApiResponse(data, followingUsersSchema);

  if (response.status === "error") {
    return [];
  } else {
    return response.data;
  }
}

export namespace UserFollowingQuery {
  export const keys = {
    all: ["user"],
    user: (userId: string) => [...keys.all, userId, "following"] as const,
  } as const;

  export function invalidate(userId?: string) {
    return queryClient.invalidateQueries({
      queryKey: userId ? keys.user(userId) : keys.all,
    });
  }

  export type TData = z.infer<typeof followingUsersSchema>;

  export const useInfiniteQuery = (userId: string, enabled = true) => {
    return useInfiniteQueryTanstack({
      queryKey: keys.user(userId),
      queryFn: getFollowing,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (lastPage.length === 0) return undefined;

        return {
          cursor: lastPage[lastPage.length - 1].createdAt.getTime(),
        };
      },
      enabled,
    });
  };
}
